import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Search, DollarSign, TrendingUp, RefreshCw,
  CheckCircle2, Clock, Filter, CreditCard, Mail, Phone, Zap,
} from "lucide-react";
import { http } from "@/lib/api";

const TYPE_META = {
  tier:       { label: "Subscription",       Icon: TrendingUp, cls: "text-indigo-800 bg-indigo-50 border-indigo-200" },
  enrichment: { label: "Enrichment credits", Icon: Zap,        cls: "text-emerald-800 bg-emerald-50 border-emerald-200" },
  email:      { label: "Email credits",      Icon: Mail,       cls: "text-sky-800 bg-sky-50 border-sky-200" },
  mobile:     { label: "Mobile credits",     Icon: Phone,      cls: "text-amber-800 bg-amber-50 border-amber-200" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16); }
};
const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminPayments() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({ revenue_all_time_usd: 0, revenue_month_usd: 0 });
  const [status, setStatus] = useState("all"); // all|paid|pending
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");

  const load = async (loud = false) => {
    if (loud) setRefreshing(true); else setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200, status, type });
      if (search.trim()) params.set("search", search.trim());
      const { data } = await http.get(`/admin/payments?${params.toString()}`);
      setPayments(data.payments || []);
      setStats(data.stats || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load payments");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [status, type]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(true), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const totals = useMemo(() => {
    const paidVisible = payments.filter((p) => p.status === "paid").reduce((s, p) => s + Number(p.total_usd || 0), 0);
    return { paidVisible };
  }, [payments]);

  const copy = (v) => { if (!v) return; navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div className="text-ink-900">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6 flex items-center gap-3">
          <Link to="/admin" className="text-[11px] uppercase tracking-widestx font-semibold text-ink-500 hover:text-ink-900 inline-flex items-center gap-1.5" data-testid="ap-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Admin
          </Link>
          <span className="text-ink-300">/</span>
          <span className="text-[11px] uppercase tracking-widestx font-semibold text-ink-900">Payments</span>
        </div>

        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-ink-900 leading-tight">
            Payments
          </h1>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="ap-refresh"
            className="text-[11px] uppercase tracking-widestx font-semibold text-ink-600 hover:text-ink-900 inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <StatCard label="Revenue · all time" value={fmt$(stats.revenue_all_time_usd)} Icon={DollarSign} accent="text-emerald-800" testId="stat-alltime" />
          <StatCard label="Revenue · this month" value={fmt$(stats.revenue_month_usd)} Icon={TrendingUp} accent="text-indigo-800" testId="stat-month" />
          <StatCard label="Paid in view" value={fmt$(totals.paidVisible)} Icon={CheckCircle2} accent="text-ink-900" testId="stat-visible" />
        </div>

        {/* Filters */}
        <div className="bg-white border border-ink-100 rounded-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-ink-500 shrink-0">
              <Filter className="w-3.5 h-3.5" /> Filter
            </div>

            <SegPill label="All" active={status === "all"} onClick={() => setStatus("all")} testId="seg-status-all" />
            <SegPill label="Paid" active={status === "paid"} onClick={() => setStatus("paid")} testId="seg-status-paid" />
            <SegPill label="Pending" active={status === "pending"} onClick={() => setStatus("pending")} testId="seg-status-pending" />

            <span className="text-ink-200">|</span>

            <SegPill label="All types" active={type === "all"} onClick={() => setType("all")} testId="seg-type-all" />
            <SegPill label="Subscription" active={type === "tier"} onClick={() => setType("tier")} testId="seg-type-tier" />
            <SegPill label="Enrichment" active={type === "enrichment"} onClick={() => setType("enrichment")} testId="seg-type-enrichment" />
            <SegPill label="Email" active={type === "email"} onClick={() => setType("email")} testId="seg-type-email" />
            <SegPill label="Mobile" active={type === "mobile"} onClick={() => setType("mobile")} testId="seg-type-mobile" />

            <div className="relative ml-auto">
              <Search className="w-3.5 h-3.5 text-ink-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Email, order id, payment id…"
                data-testid="ap-search"
                className="pl-8 pr-3 h-9 rounded-sm border border-ink-100 text-xs w-64 focus:border-ink-900 outline-none font-mono"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-ink-100 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-100">
              <tr>
                {["When", "User", "Type", "Description", "Amount", "Status", "Razorpay"].map((h) => (
                  <th key={h} className="text-left text-[10px] uppercase tracking-widestx font-mono font-semibold text-ink-500 px-4 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-ink-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
              ) : payments.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-ink-400 text-sm">No payments match the filters.</td></tr>
              ) : payments.map((p, i) => {
                const meta = TYPE_META[p.type] || TYPE_META.tier;
                const Icon = meta.Icon;
                return (
                  <tr key={i} data-testid={`ap-row-${i}`} className="hover:bg-ink-50/50">
                    <td className="px-4 py-3 text-xs text-ink-600 whitespace-nowrap font-mono">
                      {fmtDate(p.created_at)}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-semibold text-ink-900 truncate max-w-[180px]" title={p.user?.email}>{p.user?.email || "—"}</div>
                      {p.user?.tier && <div className="text-[10px] text-ink-400 font-mono uppercase">{p.user.tier}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widestx font-mono font-semibold px-2 py-0.5 rounded-sm border ${meta.cls}`}>
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-700">{p.description}</td>
                    <td className="px-4 py-3 text-xs font-mono text-right whitespace-nowrap" title={p.tax_label ? `Subtotal ${fmt$(p.subtotal_usd)} · ${p.tax_label} ${fmt$(p.tax_amount_usd)}` : ""}>
                      <div className="text-ink-900 font-semibold">{fmt$(p.total_usd)}</div>
                      {p.tax_amount_usd > 0 && (
                        <div className="text-[10px] text-ink-400">incl. {fmt$(p.tax_amount_usd)} {p.tax_label}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.status === "paid" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widestx font-mono font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">
                          <CheckCircle2 className="w-3 h-3" /> Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widestx font-mono font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm">
                          <Clock className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[10px] font-mono text-ink-500">
                      {p.razorpay_order_id && (
                        <div>
                          <button className="hover:text-ink-900 truncate max-w-[130px] inline-block align-middle" title={p.razorpay_order_id} onClick={() => copy(p.razorpay_order_id)}>
                            {p.razorpay_order_id}
                          </button>
                        </div>
                      )}
                      {p.razorpay_payment_id && (
                        <div className="text-ink-400">
                          <button className="hover:text-ink-900 truncate max-w-[130px] inline-block align-middle" title={p.razorpay_payment_id} onClick={() => copy(p.razorpay_payment_id)}>
                            {p.razorpay_payment_id}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-[10px] text-ink-400 font-mono">
          Showing latest {payments.length} · Revenue totals aggregate paid rows across all four purchase types (Subscription, Enrichment, Email, Mobile).
        </p>
      </main>
    </div>
  );
}

const StatCard = ({ label, value, Icon, accent, testId }) => (
  <div className="bg-white border border-ink-100 rounded-sm p-5 flex items-center gap-4" data-testid={testId}>
    <div className={`w-10 h-10 rounded-sm bg-ink-50 flex items-center justify-center ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">{label}</div>
      <div className={`font-display font-extrabold text-2xl mt-1 ${accent}`}>{value}</div>
    </div>
  </div>
);

const SegPill = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-[10px] uppercase tracking-widestx font-semibold px-3 h-8 rounded-sm border whitespace-nowrap ${
      active ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-600 border-ink-100 hover:border-ink-400 hover:text-ink-900"
    }`}
  >
    {label}
  </button>
);
